import {CircularProgressBar} from '@/components/MLS/CircularProgressBar/CircularProgressBar';
import {getLocationBySiteId} from '@/constants/locations';
import {isNil} from 'lodash';
import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {AppointmentResponse} from '../../../api/MLSChallenge/useGetPlayerAppointments';
import {formatDate} from './UpcomingSessions';

export interface SessionProps {
  id: string;
  reps: number;
  date: string;
  location: string;
  duration: number;
  stats: {
    accuracy: number;
    speed: number;
    awareness: number;
    passes: number;
  };
}

const StatItem = ({
  value,
  label,
  showProgressBar = false,
  isBaselineSession = false,
}: {
  value: number;
  label: string;
  showProgressBar?: boolean;
  isBaselineSession?: boolean;
}) => {
  return (
    <div className="flex flex-col items-center justify-center gap-1 xs:gap-2">
      {showProgressBar ? (
        <CircularProgressBar value={value} size={50} strokeWidth={3} />
      ) : (
        <span
          className={`font-teko text-xl xs:text-2xl font-bold ${
            isBaselineSession ? 'text-mls-baseline' : 'text-mls-sky-blue'
          }`}
          data-test-id="session-stat-value"
        >
          {label === 'Accuracy' ? `${value.toFixed(2)}%` : value.toFixed(2)}
        </span>
      )}
      <span
        className="whitespace-nowrap text-xs xs:text-sm md:text-base text-mls-secondary"
        data-test-id="session-stat-label"
      >
        {label}
      </span>
    </div>
  );
};

export const Session = ({
  session,
  isTrainer = false,
}: {
  session: AppointmentResponse['sessionSummaries']['summaries'][0];
  isTrainer?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isWhatWeDidExpanded, setIsWhatWeDidExpanded] = useState(true);

  if (!session) {
    return null;
  }

  const {
    summary: {session: {accuracy = 0, streak = 0, mph = 0, speedOfPlay = 0, score, tocaScore} = {}} = {},
    startDateTime,
    siteId,
    durationMinutes = 0,
    totalBallsFired = 0,
    _id,
    isBaselineSession = false,
    sessionOverview = '',
  } = session;

  const scoreToShow = !isNil(tocaScore) ? Math.round(tocaScore) : !isNil(score) ? Math.round(score) : null;

  const locationName = getLocationBySiteId(siteId)?.name;

  const onClickViewFullBaselineCard = () => {
    if (isTrainer) {
      // Preserve all current query parameters
      const currentSearchParams = new URLSearchParams(location.search);
      const queryString = currentSearchParams.toString();
      const targetUrl = `/trainer/connects/baseline-session-data/${_id}${queryString ? `?${queryString}` : ''}`;
      navigate(targetUrl);
    } else {
      navigate(`/baseline-session-data/${_id}`);
    }
  };

  const toggleWhatWeDid = () => {
    setIsWhatWeDidExpanded(!isWhatWeDidExpanded);
  };

  return (
    <div key={_id} className="mb-6 w-full rounded-lg bg-mls-grey" data-test-id={`session-${_id}`}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8 flex flex-row items-center gap-4 border-b border-mls-grey-dark pb-6">
          <div
            className={`flex aspect-square h-16 w-16 xs:h-20 xs:w-20 items-center justify-center rounded-full ${
              isBaselineSession ? 'bg-mls-baseline' : 'bg-mls-sky-blue'
            }`}
          >
            <span
              className="text-center font-teko text-lg xs:text-xl md:text-2xl font-bold text-white"
              data-test-id="session-reps"
            >
              {totalBallsFired}
              <br />
              REPS
            </span>
          </div>
          <div className="flex-1 w-full flex flex-col ">
            <h2 className="text-xs xs:text-sm md:text-base text-mls-secondary xs:mb-1" data-test-id="session-date">
              {startDateTime && formatDate(startDateTime.toString())}
            </h2>
            <p className="text-xs xs:text-sm md:text-base text-mls-secondary">
              {locationName && <span data-test-id="session-location">{locationName}</span>}
              {locationName && durationMinutes > 0 && ' • '}
              {durationMinutes > 0 && <span data-test-id="session-duration">{durationMinutes} min</span>}
            </p>
            {isBaselineSession ? (
              <span
                className="text-xs xs:text-sm md:text-base text-mls-baseline cursor-pointer mt-1 underline hover:text-mls-baseline-dark transition-colors duration-200"
                onClick={onClickViewFullBaselineCard}
              >
                View Detailed Baseline Card
              </span>
            ) : sessionOverview ? (
              <button
                className="flex items-center gap-1 text-xs xs:text-sm md:text-base text-mls-sky-blue hover:text-mls-sky-blue-dark mt-1 transition-all duration-200 group"
                onClick={toggleWhatWeDid}
                data-test-id="what-we-did-link"
              >
                <span className="underline group-hover:no-underline">What we did</span>
                <svg
                  className={`w-3 h-3 transition-transform duration-300 ${
                    isWhatWeDidExpanded ? 'rotate-180' : 'rotate-0'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            ) : null}
          </div>
          {!isNil(scoreToShow) && scoreToShow > 0 && (
            <div
              className={`aspect-square flex h-16 w-16 xs:h-20 xs:w-20 items-center justify-center rounded-full ${
                isBaselineSession ? 'bg-mls-baseline' : 'bg-mls-sky-blue'
              } md:ml-4`}
            >
              <span
                className="text-center font-teko text-2xl xs:text-3xl md:text-4xl font-bold text-white"
                data-test-id="session-score"
              >
                {scoreToShow}
              </span>
            </div>
          )}
        </div>

        {/* What we did expanded content */}
        {!isBaselineSession && sessionOverview && (
          <div
            className={`mb-6 overflow-hidden transition-all duration-300 ease-in-out ${
              isWhatWeDidExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="p-4 bg-mls-grey-dark rounded-lg border-l-4 border-mls-sky-blue">
              <p
                className="text-xs xs:text-sm md:text-base text-mls-secondary leading-relaxed"
                dangerouslySetInnerHTML={{__html: sessionOverview}}
              />
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2">
          <StatItem isBaselineSession={isBaselineSession} value={accuracy * 100} label="Accuracy" />
          <StatItem isBaselineSession={isBaselineSession} value={streak} label="Streak" />
          <StatItem isBaselineSession={isBaselineSession} value={mph} label="Mph" />
          <StatItem isBaselineSession={isBaselineSession} value={speedOfPlay} label="Speed of Play" />
        </div>
      </div>
    </div>
  );
};
